import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { DashboardShell } from "@/components/DashboardShell";
import { ADMIN_NAV } from "@/lib/nav";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  TICKET_STATUSES,
  TICKET_STATUS_LABEL,
  addTicketMessage,
  listAllTickets,
  listTicketMessages,
  updateTicketStatus,
  type SupportTicket,
  type TicketStatus,
} from "@/lib/support";
import {
  CategoryPill,
  ReplyBox,
  StatusPill,
  TicketHeaderCard,
  TicketHistory,
} from "@/components/support/TicketPieces";

type Tab = "all" | TicketStatus;

export const Route = createFileRoute("/_authenticated/admin/support")({
  head: () => ({
    meta: [
      { title: "Support Tickets · Landoo Admin" },
      {
        name: "description",
        content:
          "Review, reply to and resolve support tickets raised by Landoo customers and partners.",
      },
      { property: "og:title", content: "Support Tickets · Landoo Admin" },
      {
        property: "og:description",
        content: "Review, reply to and resolve Landoo support tickets.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AdminSupportPage,
});

function AdminSupportPage() {
  const { user, profile, roles } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("all");
  const [q, setQ] = useState("");
  const [active, setActive] = useState<SupportTicket | null>(null);
  const [internal, setInternal] = useState(false);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["support", "admin", "all"],
    queryFn: listAllTickets,
  });

  const { data: messages = [] } = useQuery({
    queryKey: ["support", "messages", active?.id],
    queryFn: () => listTicketMessages(active!.id),
    enabled: !!active,
  });

  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows
      .filter((r) => (tab === "all" ? true : r.status === tab))
      .filter((r) =>
        !needle
          ? true
          : [r.ticket_no, r.subject, r.user_name, r.user_email].some((v) =>
              (v ?? "").toLowerCase().includes(needle),
            ),
      );
  }, [rows, tab, q]);

  const refresh = (id?: string) => {
    qc.invalidateQueries({ queryKey: ["support", "admin", "all"] });
    if (id) qc.invalidateQueries({ queryKey: ["support", "messages", id] });
  };

  const reply = async (body: string, file: File | null) => {
    if (!active) return;
    try {
      await addTicketMessage({
        ticketId: active.id,
        authorId: user!.id,
        authorName: profile?.full_name ?? "Landoo Support",
        authorRole: roles.includes("super_admin") ? "super_admin" : "admin",
        body,
        file,
        isInternal: internal,
      });
      if (!internal && active.status === "open") {
        await updateTicketStatus(active.id, "in_progress");
        setActive({ ...active, status: "in_progress" });
      }
      toast.success(internal ? "Internal note added" : "Reply sent to user");
      refresh(active.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not send reply");
    }
  };

  const setStatus = async (status: TicketStatus) => {
    if (!active) return;
    try {
      await updateTicketStatus(active.id, status);
      setActive({ ...active, status });
      toast.success(`Status set to ${TICKET_STATUS_LABEL[status]}`);
      refresh(active.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not update status");
    }
  };

  return (
    <DashboardShell title="Support Tickets" role="Admin" items={ADMIN_NAV}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          {(["all", ...TICKET_STATUSES] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                tab === t ? "bg-brown-dark text-white" : "bg-secondary text-brown-dark"
              }`}
            >
              {t === "all" ? "All" : TICKET_STATUS_LABEL[t]}
            </button>
          ))}
        </div>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search ticket no, subject or user"
          className="w-full sm:ml-auto sm:w-72"
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        <div>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading tickets…</p>
          ) : list.length === 0 ? (
            <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              No tickets match this view.
            </p>
          ) : (
            <ul className="space-y-3">
              {list.map((t) => (
                <li key={t.id}>
                  <button
                    onClick={() => setActive(t)}
                    className={`w-full rounded-xl border p-4 text-left transition ${
                      active?.id === t.id
                        ? "border-brown-dark bg-secondary/50"
                        : "border-border bg-card"
                    }`}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusPill status={t.status} />
                      <CategoryPill category={t.category} />
                      <span className="text-[11px] font-semibold text-muted-foreground">
                        {t.ticket_no}
                      </span>
                      <span className="text-[11px] capitalize text-muted-foreground">
                        {t.user_role}
                      </span>
                    </div>
                    <p className="mt-1 text-sm font-semibold text-brown-dark">{t.subject}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {t.user_name ?? t.user_email ?? "User"} ·{" "}
                      {new Date(t.created_at).toLocaleString()}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          {!active ? (
            <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              Select a ticket to view its history and reply.
            </p>
          ) : (
            <div className="space-y-4">
              <TicketHeaderCard ticket={active} />
              <div className="flex flex-wrap gap-1.5">
                {TICKET_STATUSES.map((s) => (
                  <Button
                    key={s}
                    size="sm"
                    variant={active.status === s ? "default" : "outline"}
                    onClick={() => setStatus(s)}
                  >
                    {TICKET_STATUS_LABEL[s]}
                  </Button>
                ))}
              </div>
              <div>
                <h3 className="mb-2 text-sm font-semibold text-brown-dark">Ticket history</h3>
                <TicketHistory messages={messages} />
              </div>
              <ReplyBox
                onSend={reply}
                placeholder={
                  internal ? "Internal note (not visible to the user)…" : "Reply to the user…"
                }
                extra={
                  <label className="inline-flex items-center gap-1.5 text-xs text-brown-dark">
                    <input
                      type="checkbox"
                      checked={internal}
                      onChange={(e) => setInternal(e.target.checked)}
                    />
                    Internal note
                  </label>
                }
              />
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
